import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { Target } from 'lucide-react'
import { AlertBanner } from '../components/ui/AlertBanner'
import { KPICard } from '../components/ui/KPICard'
import { PageHeader } from '../components/ui/PageHeader'
import { YearSelector } from '../components/ui/YearSelector'
import { HoursBreakdownCard } from '../components/dashboard/HoursBreakdownCard'
import { MarginProjectsTable } from '../components/dashboard/MarginProjectsTable'
import { useMarginKPIs, useAvailableYears } from '../hooks/useKPIs'
import { useGlobalFilters } from '../contexts/GlobalFiltersContext'
import { useBUContext } from '../contexts/BUContext'

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function PanelHeader({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{label}</p>
      </div>
      {children}
    </div>
  )
}

function MarginDistributionBars({ projects }: { projects: import('../hooks/useKPIs').ProjectMargin[] }) {
  const total = projects.length
  if (total === 0) {
    return <p className="text-sm text-[--km-text-muted]">Nessuna commessa disponibile.</p>
  }

  const fasce = [
    {
      label: '≥ 30% — ottimo',
      count: projects.filter(p => p.margin_pct >= 30).length,
      color: 'bg-gradient-to-r from-emerald-600 to-emerald-400',
      textColor: 'text-emerald-400',
    },
    {
      label: '15–30% — nella norma',
      count: projects.filter(p => p.margin_pct >= 15 && p.margin_pct < 30).length,
      color: 'bg-gradient-to-r from-amber-600 to-amber-400',
      textColor: 'text-amber-400',
    },
    {
      label: '0–15% — a rischio',
      count: projects.filter(p => p.margin_pct >= 0 && p.margin_pct < 15).length,
      color: 'bg-gradient-to-r from-orange-600 to-orange-400',
      textColor: 'text-orange-400',
    },
    {
      label: '< 0% — in perdita',
      count: projects.filter(p => p.margin_pct < 0).length,
      color: 'bg-gradient-to-r from-red-700 to-red-500',
      textColor: 'text-red-400',
    },
  ]

  return (
    <div className="space-y-5" data-testid="margin-distribution-bars">
      {fasce.map(f => {
        const pct = (f.count / total) * 100
        return (
          <div key={f.label} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[--km-text-secondary] font-medium">{f.label}</span>
              <span className={`font-bold tabular-nums ${f.textColor}`}>
                {f.count} / {total}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-[#182038] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${f.color}`}
                style={{ width: `${Math.max(pct, pct > 0 ? 2 : 0)}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function MarginControl() {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number | 'cumulative'>(currentYear)
  const { data: availableYears = [currentYear] } = useAvailableYears()
  const { filters, apiDateFrom, apiDateTo } = useGlobalFilters()
  const { selectedBuId } = useBUContext()
  const { data: kpis, isLoading, error } = useMarginKPIs({
    year: selectedYear,
    apiDateFrom,
    apiDateTo,
    customer: filters.customer,
    service_line: filters.service_line,
    buId: selectedBuId,
  })

  const subtitle = kpis
    ? `Aggiornato al ${format(new Date(kpis.computed_at), "d MMM yyyy 'ore' HH:mm", { locale: it })}`
    : format(new Date(), 'd MMM yyyy', { locale: it })

  return (
    <section aria-label="Margin Control" className="space-y-6">
      <PageHeader
        title="Margin Control"
        subtitle={subtitle}
        icon={Target}
        iconColor="from-amber-500 to-orange-500"
        coveragePct={kpis?.coverage_score_pct}
        loading={isLoading}
        actions={
          <YearSelector
            years={availableYears}
            value={selectedYear}
            onChange={setSelectedYear}
            loading={isLoading}
            disabled={!!(apiDateFrom && apiDateTo)}
          />
        }
      />

      {error && (
        <AlertBanner
          severity="critical"
          message="Impossibile caricare i KPI Margin Control. Riprovare."
        />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="stagger-1">
          <KPICard
            label="Margine medio %"
            value={kpis ? formatPct(kpis.avg_margin_pct) : '—'}
            status={kpis
              ? kpis.avg_margin_pct >= 30 ? 'positive'
                : kpis.avg_margin_pct >= 15 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Media ponderata commesse attive"
          />
        </div>
        <div className="stagger-2">
          <KPICard
            label="Profittabilità totale"
            value={kpis ? formatEur(kpis.total_profitability) : '—'}
            status={kpis ? (kpis.total_profitability > 0 ? 'positive' : 'critical') : 'neutral'}
            loading={isLoading}
            subtext="Ricavi − costi totali"
          />
        </div>
        <div className="stagger-3">
          <KPICard
            label="Commesse a rischio"
            value={kpis ? String(kpis.projects_at_risk) : '—'}
            status={kpis
              ? kpis.projects_at_risk === 0 ? 'positive'
                : kpis.projects_at_risk <= 2 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Margine < 15%"
          />
        </div>
        <div className="stagger-4">
          <KPICard
            label="Commesse in perdita"
            value={kpis ? String(kpis.projects_in_loss) : '—'}
            status={kpis ? (kpis.projects_in_loss === 0 ? 'positive' : 'critical') : 'neutral'}
            loading={isLoading}
            subtext="Margine negativo"
          />
        </div>
      </div>

      {/* Hours breakdown + Margin distribution */}
      {!isLoading && kpis && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <HoursBreakdownCard
              customerHours={kpis.total_hours_customer}
              rdHours={kpis.total_hours_rd}
              utilizationPct={kpis.utilization_pct}
            />
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
            <PanelHeader label="Distribuzione commesse per fascia margine" />
            <MarginDistributionBars projects={kpis.projects} />
          </div>
        </div>
      )}

      {/* Drill-down table */}
      {!isLoading && kpis && (
        <MarginProjectsTable projects={kpis.projects} />
      )}

      {kpis && kpis.coverage_score_pct < 80 && (
        <AlertBanner
          severity="warning"
          message={`Copertura dati: ${kpis.coverage_score_pct.toFixed(0)}% dei KPI calcolabili. Caricare più dati per ottenere un quadro completo.`}
        />
      )}
    </section>
  )
}
